import React from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ChartHeader from "examples/Charts/General/ChartHeader";
import { motion } from "framer-motion";
import ChartAssistant from "components/ChartAssistant";
import { generateStrategicNarrative } from "../utils/strategicInsights";

function StrategicInsightsChart({
  title = "Market Landscape Insights",
  description = null,
  datasets,
  previousdatasets,
  adjustedcurrentdatasets,
  adjustedpreviousdatasets,
  isLoading = false,
  entitlements // passed through
}) {
  if (isLoading) {
    return (
      <Card>
        <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
          <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
      </Card>
    );
  }

  if (!datasets || datasets.length === 0) {
    return (
      <Card>
        <MDBox p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No profile data available for Strategic Insights.
          </Typography>
        </MDBox>
      </Card>
    );
  }

  const tooltipContent = (
    <div style={{ maxWidth: "380px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        Provides AI-driven Gap Analysis comparing your brand's strategy to competitor market leaders.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the insights:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - <strong>Recovery/Growth Strategy</strong>: Identifies if your audience is declining or expanding. <br />
        - <strong>Format Efficiency</strong>: Compares your top content formats vs. the market consensus. <br />
        - <strong>Authority/Reactivation</strong>: Detects if your engagement share matches your audience size.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Identify "Competitive Gaps" in your posting volume or format mix. <br />
        - Detect "Discovery Bottlenecks" where high engagement isn't translating to growth. <br />
        - Get actionable advice on whether to pivot content or scale frequency.
      </Typography>
    </div>
  );

  const profilePictures = datasets.map((p) => p.profilePictureUrl || "https://via.placeholder.com/150");

  const insights = generateStrategicNarrative(
    datasets,
    previousdatasets,
    adjustedcurrentdatasets,
    adjustedpreviousdatasets
  );

  return (
    <Card sx={{ display: "flex", flexDirection: "column", pb: 2, overflow: "visible", position: "relative" }}>
      <ChartHeader
        title={title}
        description={description || tooltipContent}
        profilePictures={profilePictures}
      >
        <ChartAssistant
          title="Market Landscape Insights"
          context="Strategic Insights Gap Analysis"
          chartData={{ datasets }}
          rawData={datasets}
          sx={{ position: "static", ml: 2 }}
          entitlements={entitlements}
        />
      </ChartHeader>

      <MDBox
        component={motion.div}
        layout="position"
        px={3}
        py={2}
        display="flex"
        flexDirection="column"
        sx={{
          backgroundColor: "transparent",
          width: "100%",
          margin: "0 auto",
        }}
      >
        <Grid container spacing={2}>
          {insights.map((insight, index) => (
            <Grid item xs={12} key={index}>
              <MDBox
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                px={3}
                py={2.5}
                display="flex"
                flexDirection="column"
                sx={({ palette: { grey, [insight.color || "info"]: colorMain }, borders: { borderRadius } }) => ({
                  backgroundColor: grey[100],
                  borderRadius: borderRadius.xl,
                  borderLeft: `6px solid ${colorMain.main}`,
                  position: "relative",
                  boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateX(4px)",
                    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
                  }
                })}
              >
                <MDBox display="flex" alignItems="center" mb={1.5}>
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="32px"
                    height="32px"
                    bgColor={insight.color || "info"}
                    color="white"
                    borderRadius="md"
                    mr={2}
                    shadow="md"
                  >
                    <Icon fontSize="small">{insight.icon || "lightbulb"}</Icon>
                  </MDBox>
                  <MDTypography variant="button" fontWeight="bold" color={insight.color || "info"} textTransform="uppercase" letterSpacing={1}>
                    {insight.category}
                  </MDTypography>
                </MDBox>

                <MDTypography variant="body2" color="text" fontWeight="regular" sx={{ mb: 1, lineHeight: 1.6 }}>
                  {insight.description}
                </MDTypography>

                <MDBox 
                  mt={1} 
                  p={1.5} 
                  bgColor="white" 
                  borderRadius="lg" 
                  sx={({ palette: { [insight.color || "info"]: colorMain } }) => ({
                    border: `1px dashed ${colorMain.main}40`,
                  })}
                >
                  <MDTypography variant="body2" color="text" fontWeight="bold">
                    Target Action:
                    <MDTypography component="span" variant="body2" color="text" fontWeight="regular" ml={1}>
                      {insight.targetAction}
                    </MDTypography>
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
          ))}
        </Grid>

        <MDBox 
          px={3} 
          pb={1} 
          mt={3} 
          display="flex" 
          flexWrap="wrap" 
          gap={3} 
          sx={({ palette: { grey } }) => ({ 
            borderTop: `1px solid ${grey[200]}`, 
            pt: 2,
            opacity: 0.8
          })}
        >
          <MDBox display="flex" alignItems="center">
            <MDBox width="8px" height="8px" bgColor="success" borderRadius="50%" mr={1} shadow="sm" />
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Growth & Momentum
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <MDBox width="8px" height="8px" bgColor="info" borderRadius="50%" mr={1} shadow="sm" />
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Health & ROI Audits
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <MDBox width="8px" height="8px" bgColor="warning" borderRadius="50%" mr={1} shadow="sm" />
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Strategic Gaps
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <MDBox width="8px" height="8px" bgColor="error" borderRadius="50%" mr={1} shadow="sm" />
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Critical Recovery
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

StrategicInsightsChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.node,
  datasets: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  entitlements: PropTypes.object,
};

export default StrategicInsightsChart;
